import type { RoleCatalog } from '../../../store/user/userV2/reducer';

export type { RoleCatalog };

export interface RoleOption {
    id: string;
    label: string;
    description: string;
    userType?: string;
}

export interface RoleOptions {
    options: RoleOption[];
    defaultId: string;
}

/**
 * Build dropdown options from the per-PNO role catalog.
 * Returns options where assignable !== false, in config-definition (insertion) order.
 * Falls back to DEFAULT_TEAM_ROLE or first option when no default entry found.
 */
export function buildRoleOptions(roleCatalog: RoleCatalog): RoleOptions {
    const entries = Object.entries(roleCatalog).filter(([, v]) => v.assignable !== false);
    const options: RoleOption[] = entries.map(([key, v]) => ({
        id: key,
        label: v.viewName || key,
        description: v.description || '',
        ...(v.userType && { userType: v.userType }),
    }));
    const defaultEntry = entries.find(([, v]) => v.default === true);
    const defaultId = defaultEntry ? defaultEntry[0] : DEFAULT_TEAM_ROLE || options[0]?.id || '';
    return { options, defaultId };
}

export interface UserTypeCatalogEntry {
    userType: string;
    label: string;
    default?: boolean;
}

/**
 * Build dropdown options from a per-PNO userType catalog (external user types,
 * e.g. Lender Admin / Team Member). Produces the same RoleOptions shape as
 * buildRoleOptions so both feed the shared Dropdown component.
 */
export function buildUserTypeOptions(entries: UserTypeCatalogEntry[] | undefined): RoleOptions {
    const list = entries ?? [];
    const options: RoleOption[] = list.map((e) => ({
        id: e.userType,
        label: e.label,
        description: '',
        userType: e.userType,
    }));
    const defaultEntry = list.find((e) => e.default === true);
    const defaultId = defaultEntry ? defaultEntry.userType : options[0]?.id ?? '';
    return { options, defaultId };
}

export const MANAGE_USER_EXCLUDED_LABELS = ['City'];

export const USER_ROLE_KEY = 'userRole';
export const USER_TYPE_DROPDOWN_KEY = 'userTypeDropdown';
export const DEFAULT_TEAM_ROLE = 'SALES_MANAGER';
export const SUPER_ADMIN_ROLE = 'SUPER_ADMIN';
export const TEAM_USERS_PAGE_SIZE = 200;
export const ROLE_ASSIGN_RETRY_MS = 1500;
export const ROLE_ASSIGN_FAILED_MSG = 'User created, but role assignment failed. You can set the role via Edit User.';

export const USER_TYPE_OPTIONS = [
    { id: 'INSIDE_SALES', label: 'Prospect Manager' },
    { id: 'BD', label: 'Lead Manager' },
    { id: DEFAULT_TEAM_ROLE, label: 'Deal Manager' },
    { id: 'LENDER_MANAGER', label: 'Lender Manager' },
    { id: 'PARTNER_MANAGER', label: 'Partner Manager' },
    { id: SUPER_ADMIN_ROLE, label: 'Super Admin' },
];

export const ManageUserTableHeaders: {
    id: number;
    label: string;
    type: 'static' | 'dynamic';
}[] = [
    {
        id: 0,
        label: 'User Name',
        type: 'static',
    },
    {
        id: 1,
        label: 'Email',
        type: 'static',
    },
    {
        id: 2,
        label: 'Phone Number',
        type: 'static',
    },
    {
        id: 3,
        label: 'Job Role',
        type: 'static',
    },
    {
        id: 4,
        label: 'User Type',
        type: 'static',
    },
    {
        id: 5,
        label: 'Status',
        type: 'static',
    },
];

export const AllUsersTableHeaders: {
    id: number;
    label: string;
    type: 'static' | 'dynamic';
}[] = [
    {
        id: 0,
        label: 'User Name',
        type: 'static',
    },
    {
        id: 1,
        label: 'Organisation',
        type: 'static',
    },
    {
        id: 2,
        label: 'Email',
        type: 'static',
    },
    {
        id: 3,
        label: 'Phone Number',
        type: 'static',
    },
    {
        id: 4,
        label: 'Job Role',
        type: 'static',
    },
    {
        id: 5,
        label: 'User Type',
        type: 'static',
    },
    {
        id: 6,
        label: 'Status',
        type: 'static',
    },
    {
        id: 7,
        label: 'Actions',
        type: 'static',
    },
];

export const InvestorTableHeaders: {
    id: number;
    label: string;
    type: 'static' | 'dynamic';
}[] = [
    {
        id: 0,
        label: 'Lender Name',
        type: 'static',
    },
    {
        id: 1,
        label: 'Profile Type',
        type: 'static',
    },
    {
        id: 2,
        label: 'Users',
        type: 'static',
    },
    {
        id: 3,
        label: 'Lender Owner',
        type: 'static',
    },
    {
        id: 4,
        label: 'Internal Comments',
        type: 'static',
    },
    {
        id: 5,
        label: 'Investment Pref.',
        type: 'static',
    },
    {
        id: 6,
        label: 'Fee Aligned',
        type: 'static',
    },
    {
        id: 7,
        label: 'Actions',
        type: 'static',
    },
];

export const CompanySideHeaders: {
    id: number;
    label: string;
    type: 'static' | 'dynamic';
}[] = [
    {
        id: 0,
        label: 'User Name',
        type: 'static',
    },
    {
        id: 1,
        label: 'Phone Number',
        type: 'static',
    },
    {
        id: 2,
        label: 'Email',
        type: 'static',
    },
    {
        id: 3,
        label: 'Job Role',
        type: 'static',
    },
    {
        id: 4,
        label: 'User Type',
        type: 'static',
    },
    {
        id: 5,
        label: 'Status',
        type: 'static',
    },
    {
        id: 6,
        label: 'Actions',
        type: 'static',
    },
];

export const PartnerTableHeaders: {
    id: number;
    label: string;
    type: 'static' | 'dynamic';
}[] = [
    {
        id: 0,
        label: 'Partner Name',
        type: 'static',
    },
    {
        id: 1,
        label: 'Users',
        type: 'static',
    },
    {
        id: 2,
        label: 'Partner Owner',
        type: 'static',
    },
    {
        id: 3,
        label: 'Internal Comments',
        type: 'static',
    },
    {
        id: 4,
        label: 'Actions',
        type: 'static',
    },
];
